// Unified dictation hook built on top of Web Speech API
// Provides stable start/stop, single active session across app, and safety timeout.

import { useCallback, useEffect, useRef, useState } from 'react';

// Singleton manager to enforce single recognition session across app
const DictationManager = (() => {
    let recognition = null;
    let isListening = false;
    let listeners = new Set();
    let safetyTimer = null;
    let currentOnResult = null;
    let currentOnStart = null;
    let currentOnEnd = null;
    let currentOnError = null;
    let micStream = null;
    let sessionToken = 0;

    const notify = () => {
        const supported = typeof window !== 'undefined' && (
            'SpeechRecognition' in window || 'webkitSpeechRecognition' in window
        );
        const state = { isListening, supported };
        listeners.forEach((fn) => {
            try { fn(state); } catch (e) { /* ignore */ }
        });
    };

    const stopMediaStream = () => {
        if (micStream) {
            try { micStream.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
            micStream = null;
        }
    };

    const clearSafetyTimer = () => {
        if (safetyTimer) {
            clearTimeout(safetyTimer);
            safetyTimer = null;
        }
    };

    const cleanup = () => {
        clearSafetyTimer();
        stopMediaStream();
        currentOnResult = null;
        currentOnStart = null;
        currentOnEnd = null;
        currentOnError = null;
        isListening = false;
        recognition = null;
        notify();
    };

    const primeMic = async () => {
        try {
            if (navigator.mediaDevices?.getUserMedia) {
                micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                // Immediately release, SR manages its own capture
                stopMediaStream();
            }
        } catch (e) {
            // Permission denied or not available
        }
    };

    const start = async ({ onResult, lang = 'en-US', interimResults = false, continuous = false, safetyTimeoutMs = 15000, onStart, onEnd, onError, prime = true } = {}) => {
        const supported = typeof window !== 'undefined' && (
            'SpeechRecognition' in window || 'webkitSpeechRecognition' in window
        );
        if (!supported) {
            return { started: false, reason: 'unsupported' };
        }
        // Detect iOS Safari (including iPadOS)
        const ua = (typeof navigator !== 'undefined' && (navigator.userAgent || navigator.vendor)) || '';
        const isIOS = /iPad|iPhone|iPod/.test(ua) || (/(Macintosh)/.test(ua) && typeof navigator !== 'undefined' && 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 1);

        // Stop previous session if any
        if (recognition && isListening) {
            try { recognition.stop(); } catch (e) { /* ignore */ }
        }

        // Prepare mic permission if requested
        // IMPORTANT: On iOS Safari, awaiting getUserMedia before rec.start() can break user-gesture gating
        // Skip priming on iOS to maximize reliability
        if (prime && !isIOS) {
            await primeMic();
        }

        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const localToken = ++sessionToken;
        const rec = new SR();
        recognition = rec;
        rec.continuous = !!continuous; // recommend false for stability
        rec.interimResults = !!interimResults; // recommend false for stability
        rec.lang = lang || 'en-US';
        rec.maxAlternatives = 1;

        currentOnResult = typeof onResult === 'function' ? onResult : null;
        currentOnStart = typeof onStart === 'function' ? onStart : null;
        currentOnEnd = typeof onEnd === 'function' ? onEnd : null;
        currentOnError = typeof onError === 'function' ? onError : null;

        rec.onstart = () => {
            if (sessionToken !== localToken) return;
            isListening = true;
            notify();
            try { currentOnStart && currentOnStart(); } catch (e) { /* ignore */ }
        };

        rec.onresult = (event) => {
            if (sessionToken !== localToken) return;
            let transcript = '';
            try {
                // Aggregate final results only
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    if (event.results[i].isFinal) {
                        transcript += event.results[i][0]?.transcript || '';
                        transcript += ' ';
                    }
                }
                transcript = transcript.trim();
            } catch (e) {
                // ignore
            }
            if (transcript && currentOnResult) {
                try { currentOnResult(transcript); } catch (e) { /* ignore */ }
            }
            // On iOS Safari, let the recognizer end naturally to avoid 'aborted'
            if (!isIOS) {
                try { rec.stop(); } catch (e) { /* ignore */ }
            }
        };

        rec.onerror = (event) => {
            if (sessionToken !== localToken) return;
            clearSafetyTimer();
            // Treat 'aborted' and 'no-speech' as benign (common when we intentionally stop or nothing was said)
            try {
                const code = event && event.error;
                if (code !== 'aborted' && code !== 'no-speech') {
                    currentOnError && currentOnError(event);
                }
            } catch (e) { /* ignore */ }
            // Let onend handle final cleanup
        };

        rec.onend = () => {
            if (sessionToken !== localToken) return;
            clearSafetyTimer();
            try { currentOnEnd && currentOnEnd(); } catch (e) { /* ignore */ }
            cleanup();
        };

        try {
            rec.start();
        } catch (e) {
            cleanup();
            return { started: false, reason: 'start_failed', error: e };
        }

        // Safety timeout
        clearSafetyTimer();
        safetyTimer = setTimeout(() => {
            if (sessionToken !== localToken) return;
            if (recognition && isListening) {
                try { recognition.stop(); } catch (e) { /* ignore */ }
            }
        }, Math.max(3000, safetyTimeoutMs || 15000));

        return { started: true };
    };

    const stop = () => {
        clearSafetyTimer();
        if (recognition) {
            try { recognition.stop(); } catch (e) { /* ignore */ }
        }
        // onend will cleanup
    };

    const subscribe = (fn) => {
        listeners.add(fn);
        // initial notify
        fn({ isListening, supported: typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) });
        return () => listeners.delete(fn);
    };

    return {
        start,
        stop,
        subscribe,
        getState: () => ({ isListening, supported: typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) }),
    };
})();

export default function useDictation(defaultOptions = {}) {
    const [state, setState] = useState(DictationManager.getState());
    const optsRef = useRef(defaultOptions);

    useEffect(() => {
        optsRef.current = defaultOptions || {};
    }, [defaultOptions]);

    useEffect(() => {
        return DictationManager.subscribe(setState);
    }, []);

    const start = useCallback(async ({ onResult, onStart, onEnd, onError, lang, interimResults, continuous, safetyTimeoutMs, prime } = {}) => {
        const merged = {
            ...optsRef.current,
            onResult,
            onStart,
            onEnd,
            onError,
            lang: lang ?? optsRef.current.lang ?? 'en-US',
            interimResults: interimResults ?? optsRef.current.interimResults ?? false,
            continuous: continuous ?? optsRef.current.continuous ?? false,
            safetyTimeoutMs: safetyTimeoutMs ?? optsRef.current.safetyTimeoutMs ?? 15000,
            prime: prime ?? optsRef.current.prime ?? true,
        };
        return DictationManager.start(merged);
    }, []);

    const stop = useCallback(() => {
        DictationManager.stop();
    }, []);

    return {
        isSupported: state.supported,
        isListening: state.isListening,
        start,
        stop,
    };
}
